import React, { useRef, useState } from 'react';

import { AiField } from 'components/ai/field';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';
import TaggedBodyInput from 'bundles/apex/deals/tagged_body/input';

import PanelFooter from './panel_footer';

import type { Activity } from 'bundles/apex/activities/types';

interface TagifyDropdownEvent {
  detail: { tagify: { dropdown: { hide: () => void } } };
}

// Tagify's own placeholder is set once at mount, and the tagging input stays mounted across tabs.
const TAGGING_PLACEHOLDER = 'Use "@" to tag a contact or user...';

interface Props {
  aiEnabled?: boolean;
  bodyParts?: Activity['bodyParts'];
  children?: React.ReactNode;
  collapseKey?: string;
  parentId?: number;
  parentType?: string;
  purpose: string;
  buttonLabel: string;
  date: Date;
  placeholder: string;
  saving: boolean;
  seeded?: boolean;
  // Swaps the AI textarea for an @-tagging input that lists the tags this path returns.
  tagsPath?: string;
  value: string;
  onChange: (value: string) => void;
  onDateChange: (date: Date) => void;
  onSubmit: () => void;
}

export default function ActivityPanel({
  aiEnabled,
  bodyParts,
  children,
  collapseKey,
  parentId,
  parentType,
  purpose,
  buttonLabel,
  date,
  placeholder,
  saving,
  seeded,
  tagsPath,
  value,
  onChange,
  onDateChange,
  onSubmit,
}: Props) {
  const [generating, setGenerating] = useState(false);
  // Seeded tags alone aren't an activity yet.
  const hasText = Boolean((seeded ? value.replace(/\[\[.*?\]\]/g, '') : value).trim());
  const openMentions = useRef<TagifyDropdownEvent['detail']['tagify'] | null>(null);

  // Tagify closes its mention list from a window listener, which an enclosing dialog's Escape-to-close
  // beats, so Escape closes the list here and goes no further.
  const closeMentions = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !openMentions.current) return;

    e.stopPropagation();
    openMentions.current.dropdown.hide();
  };

  return (
    <div className="d-flex flex-column gap-3">
      {tagsPath ? (
        <div onKeyDown={closeMentions}>
          <TaggedBodyInput
            availableTagsPath={tagsPath}
            body={value}
            bodyParts={bodyParts}
            placeholder={TAGGING_PLACEHOLDER}
            onChange={onChange}
            onDropdownHide={() => {
              openMentions.current = null;
            }}
            onDropdownShow={(event: TagifyDropdownEvent) => {
              openMentions.current = event.detail.tagify;
            }}
          />
        </div>
      ) : (
        <AiField
          aiEnabled={Boolean(aiEnabled && parentId && parentType)}
          collapseKey={collapseKey}
          context={{ parentId, parentType, purpose }}
          feature="Activity body"
          value={value}
          onChange={onChange}
          onGeneratingChange={setGenerating}
        >
          {({ className, generating }) => (
            <Textarea
              className={className}
              placeholder={placeholder}
              readOnly={generating}
              rows={4}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            />
          )}
        </AiField>
      )}
      {children}
      <PanelFooter
        buttonLabel={buttonLabel}
        date={date}
        disabled={generating || !hasText}
        saving={saving}
        showDate={hasText}
        onDateChange={onDateChange}
        onSubmit={onSubmit}
      />
    </div>
  );
}
